import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from '@/store'
import { loadChatMessages } from '@/store/slices/ragChatSlice'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { ChatSuggestions } from './ChatSuggestions'
import { 
  ClockIcon, 
  SparklesIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline'

export const ChatWindow: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>()
  const {
    messages,
    currentSession,
    isLoading,
    isTyping,
    showSuggestions,
    suggestions
  } = useSelector((state: RootState) => state.ragChat)
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  // 當會話變更時載入訊息
  useEffect(() => {
    if (currentSession && messages.length === 0) {
      dispatch(loadChatMessages(currentSession.id))
    }
  }, [currentSession, messages.length, dispatch])

  // 自動滾動到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, isTyping])

  // 如果沒有當前會話
  if (!currentSession) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <SparklesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">正在初始化 AI 助手...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col bg-white h-full">
      {/* 會話信息欄 */}
      <div className="flex-shrink-0 px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-xs text-gray-600">
            <ClockIcon className="h-3 w-3" />
            <span>
              {new Date(currentSession.updatedAt).toLocaleString()}
            </span>
          </div>
          
          {currentSession.context?.currentEntity && (
            <div className="flex items-center space-x-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              <DocumentTextIcon className="h-3 w-3" />
              <span>
                {currentSession.context.currentEntity.title || 
                 currentSession.context.currentEntity.id}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* 訊息區域 */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0"
      >
        {messages.length === 0 && !isLoading ? (
          <div className="h-full flex flex-col items-center justify-center text-center">
            <SparklesIcon className="h-16 w-16 text-blue-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">
              歡迎使用 AI 助手！
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-xs">
              我可以協助您搜尋專案資訊、分析議題內容，以及回答相關問題。
            </p>
            
            {/* 顯示建議 */}
            {suggestions.length > 0 && (
              <ChatSuggestions suggestions={suggestions.slice(0, 4)} />
            )}
          </div>
        ) : (
          <>
            {/* 渲染所有訊息 */}
            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            
            {/* 打字指示器 */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="max-w-xs lg:max-w-md bg-gray-100 rounded-lg px-4 py-2">
                  <div className="flex space-x-1">
                    <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                    <div className="h-2 w-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                  </div>
                </div>
              </div>
            )}
            
            {/* 載入指示器 */}
            {isLoading && (
              <div className="flex justify-center py-4">
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                  <span>AI 正在思考中...</span>
                </div>
              </div>
            )}
          </>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* 建議區域 */}
      {showSuggestions && messages.length > 0 && suggestions.length > 0 && (
        <div className="flex-shrink-0 px-4 py-2 border-t border-gray-100 bg-gray-50">
          <div className="text-xs text-gray-500 mb-2">建議查詢:</div>
          <ChatSuggestions suggestions={suggestions.slice(0, 3)} compact />
        </div>
      )}

      {/* 輸入區域 */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        <ChatInput />
      </div>
    </div>
  )
}

export default ChatWindow
